/* eslint-disable prettier/prettier */
import React, { useState, useMemo, useEffect } from 'react';
import styles from '../styles/pages/admin/AdminProgramsPage.module.css';
import ProgramCard from '../components/pageComponents/program/ProgramCard';
import DropdownFilter from '../components/controlVariations/DropdownFilter';
import ProgramForm from '../components/pageComponents/program/ProgramForm';
import { useNavigate } from 'react-router-dom';
import { useCourseCatalog } from '../hooks/useCourse';
import { CourseSummary } from '../types/course';
import { useHeader } from '../context/HeaderContext';
import { mapCourseToProgramData } from '../utils/courseMapper';
import { profile } from 'console';
import { useUserProfile } from '../hooks/useUserProfile';
import { apiService } from '../services/api.service';
import { ProgramData } from '../types/program';
import { useCoachesList } from '../hooks/useCoachesList';
import { DatePicker } from '../components/common/DatePicker';

import EnrollmentModal, {
    User,
} from '../components/pageComponents/program/EnrollmentModal';

import { useCourseBulkEnrollment } from '../hooks/useCourseBulkEnrollment';

import ResponsiveModal from '../components/controlVariations/ResponsiveModal';
import SuccessContent from '../components/controlVariations/ModularSuccessModal';
import ErrorPopupModal from '../components/controlVariations/ErrorPopupMoadal';

const ProgramsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const { setHeaderContent } = useHeader();
    const { courses, isLoading, error } = useCourseCatalog();
    const { profile } = useUserProfile();
    const [isCreating, setIsCreating] = useState(false);
    const [programsWithImages, setProgramsWithImages] = useState<ProgramData[]>([]);

    // State for filters and their handlers
    const [filters, setFilters] = useState({
        category: '',
        coach: '',
        dateRange: {
            start: null as Date | null,
            end: null as Date | null,
        },
    });

    const [showDatePicker, setShowDatePicker] = useState(false);

    const [localCourses, setLocalCourses] = useState<CourseSummary[]>([]);

    useEffect(() => {
        if (courses) {
            setLocalCourses(courses);
        }
    }, [courses]);

    const [sortBy, setSortBy] = useState<'latest' | 'oldest' | 'title'>(
        'latest'
    );

    // Toggle logic
    // const handleCreateClick = () => setIsCreating(true);
    const handleCancelCreate = () => setIsCreating(false);

    const handleFilterChange = (key: string, value: string | number) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        // Here you would trigger your backend filtering or local array filter logic
    };
    const categoryOptions = useMemo(() => {
        if (!localCourses) return [];

        const uniqueCategories = Array.from(
            new Set(courses.map(c => c.solutiontitle))
        );

        return [
            { label: 'All Solutions', value: '' },
            ...uniqueCategories.map(cat => ({
                label: cat,
                value: cat,
            })),
        ];
    }, [courses]);

    const { coaches, loading } = useCoachesList('coach');

    const coachOptions = useMemo(() => {
        return [
            { label: 'All Coaches', value: '' },
            ...coaches.map(coach => ({
                label: coach.fullname,
                value: coach.id, // 🔥 ALWAYS use ID
            })),
        ];
    }, [coaches]);

    // Set header content on mount
    useEffect(() => {
        setHeaderContent(
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                    Program List
                </h2>
            </div>
        );
        return () => {
            setHeaderContent(null); // cleanup on unmount
        };
    }, []);

    useEffect(() => {
        // If we are in "Create Mode", we might want a different header or no header
        if (isCreating) {
            setHeaderContent(null);
            return;
        }

        setHeaderContent(
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                    Program List
                </h2>
            </div>
        );
        return () => setHeaderContent(null);
    }, [isCreating, setHeaderContent]);

    // Call hook without arguments so 'filters' is undefined
    // and no query string is sent to your backend.

    const basePath = React.useMemo(() => {
        if (profile?.rolename === 'guest') return '/dashboard';
        else if (profile?.rolename === 'coach') return '/dashboard';
        else if (profile?.rolename === 'coachee') return '/dashboard';
        else return '/dashboard';
    }, [profile?.rolename]);

    const handleViewProgram = (id: string | number) => {
        navigate(`${basePath}/programs/${id}`);
    };

    const filteredCourses = useMemo(() => {
        if (!localCourses) return [];

        return localCourses.filter(course => {
            const matchesSearch =
                course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.category
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());

            const matchesCategory =
                !filters.category || course.solutiontitle === filters.category;

            const matchesCoach =
                !filters.coach ||
                (course.faculty ?? []).some(
                    faculty => faculty.userid === filters.coach
                );



            const matchesDate = (() => {
                const { start, end } = filters.dateRange;

                if (!start || !end) return true;

                const filterStart = new Date(start);
                filterStart.setHours(0, 0, 0, 0);

                const filterEnd = new Date(end);
                filterEnd.setHours(23, 59, 59, 999);

                const courseStart = new Date(course.startdate);
                const courseEnd = new Date(course.enddate);

                return (
                    courseStart >= filterStart &&
                    courseEnd <= filterEnd
                );
            })();

            const matchesStatus =
                course.status.toLowerCase() === "published";

            return (
                matchesSearch &&
                matchesCategory &&
                matchesCoach &&
                matchesDate &&
                matchesStatus
            );
        });
    }, [localCourses, searchTerm, filters]);


    const sortedCourses = useMemo(() => {
        const sorted = [...filteredCourses];

        switch (sortBy) {
            case 'latest':
                return sorted.sort(
                    (a, b) =>
                        new Date(b.createdat).getTime() -
                        new Date(a.createdat).getTime()
                );

            case 'oldest':
                return sorted.sort(
                    (a, b) =>
                        new Date(a.createdat).getTime() -
                        new Date(b.createdat).getTime()
                );

            case 'title':
                return sorted.sort((a, b) => a.title.localeCompare(b.title));

            default:
                return sorted;
        }
    }, [filteredCourses, sortBy]);

    useEffect(() => {
        const loadProgramsWithImages = async () => {
            if (!sortedCourses?.length) {
                setProgramsWithImages([]);
                return;
            }

            const mapped = sortedCourses.map(mapCourseToProgramData);

            setProgramsWithImages(mapped);

            const enriched = await Promise.all(
                mapped.map(async program => {
                    try {
                        const res = await apiService.get<{ picture: string }>(
                            `course/${program.id}/cover`
                        );

                        return {
                            ...program,
                            imageUrl: res?.data?.picture,
                        };
                    } catch {
                        return program;
                    }
                })
            );

            setProgramsWithImages(enriched);
        };

        loadProgramsWithImages();
    }, [sortedCourses]);


    const [isEnrollmentOpen, setIsEnrollmentOpen] = useState(false);

    const [selectedProgram, setSelectedProgram] =
        useState<ProgramData | null>(null);

    const [learners, setLearners] = useState<User[]>([]);

    const [successModal, setSuccessModal] = useState({
        open: false,
        title: '',
        subtitle: '',
    });

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const {
        bulkEnroll,
        getCoacheesList,
        bulkEnrollLoading,
    } = useCourseBulkEnrollment();

    const handleOpenEnrollment = async (program: ProgramData) => {
        try {
            setSelectedProgram(program);

            const coachees = await getCoacheesList({
                type: 'coachee',
                unassigned: true,
                courseId: program.id,
            });

            const mappedUsers: User[] = coachees.map(user => ({
                id: user.id,
                name: user.fullname,
                avatarUrl: user.profilepicture,
            }));

            setLearners(mappedUsers);
            setIsEnrollmentOpen(true);
        } catch (err) {
            setErrorMessage('Failed to load learners.');
            setShowErrorModal(true);
        }
    };

    const handleEnrollLearners = async (selectedUserIds: string[]) => {
        if (!selectedProgram) return;

        try {
            const response = await bulkEnroll(
                selectedProgram.id,
                selectedUserIds
            );

            if (!response) {
                setErrorMessage('Failed to enroll learners.');
                setShowErrorModal(true);
                return;
            }

            setIsEnrollmentOpen(false);
            setSelectedProgram(null);
            setLearners([]);

            setSuccessModal({
                open: true,
                title: 'Learners Enrolled Successfully',
                subtitle:
                    'Selected Learners have been successfully enrolled in the program.',
            });

            setTimeout(() => {
                setSuccessModal(prev => ({
                    ...prev,
                    open: false,
                }));
            }, 3000);
        } catch (err: any) {
            setErrorMessage(
                err?.message || 'Failed to enroll learners.'
            );
            setShowErrorModal(true);
        }
    };

    const handleCloseEnrollment = () => {
        setIsEnrollmentOpen(false);
        setSelectedProgram(null);
        setLearners([]);
    };
    if (error) return <div className={styles.errorState}>Error: {error}</div>;

    return (
        <div className={styles.pageContainer}>
            {isCreating ? (
                // 1. Render the Form if isCreating is true
                <ProgramForm onBack={handleCancelCreate} />
            ) : (
                // 2. Render the List if isCreating is false
                <>
                    <div className={styles.headerSection}>
                        {/* Left Side: Filters */}
                        <div className={styles.filtersLeft}>
                            <DropdownFilter
                                placeholder="All Categories"
                                options={categoryOptions}
                                selectedValue={filters.category}
                                onSelect={val =>
                                    handleFilterChange('category', val)
                                }
                            />

                            <DropdownFilter
                                placeholder="All Coaches"
                                options={coachOptions}
                                selectedValue={filters.coach}
                                onSelect={val =>
                                    handleFilterChange('coach', val)
                                }
                            />
                            <div className={styles.dateWrapper}>
                                <div
                                    className={styles.dateFilter}
                                    onClick={() =>
                                        setShowDatePicker(prev => !prev)
                                    }
                                >
                                    {filters.dateRange.start &&
                                        filters.dateRange.end ? (
                                        `${filters.dateRange.start.toDateString()} - ${filters.dateRange.end.toDateString()}`
                                    ) : (
                                        <>
                                            <span
                                                className={styles.placeholder}
                                            >
                                                Date Range
                                            </span>
                                            <svg
                                                className={
                                                    showDatePicker
                                                        ? styles.rotate
                                                        : ''
                                                }
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="13"
                                                height="8"
                                                viewBox="0 0 13 8"
                                                fill="none"
                                            >
                                                <path
                                                    d="M1 1L6.5 6.5L12 1"
                                                    stroke="#949494"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </>
                                    )}
                                </div>

                                {showDatePicker && (
                                    <div className={styles.datePopover}>
                                        <DatePicker
                                            initialStartDate={
                                                filters.dateRange.start
                                            }
                                            initialEndDate={
                                                filters.dateRange.end
                                            }
                                            onSelect={(start, end) => {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    dateRange: { start, end },
                                                }));
                                                setShowDatePicker(false);
                                            }}
                                            onCancel={() =>
                                                setShowDatePicker(false)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Side */}
                        <div className={styles.actionRight}>
                            {/* Empty intentionally */}
                        </div>
                    </div>

                    {isLoading ? (
                        <div className={styles.loaderGrid}>
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className={styles.skeletonCard} />
                            ))}
                        </div>
                    ) : (
                        <div className={styles.programGrid}>
                            {programsWithImages.length > 0 ? (
                                programsWithImages.map((program: ProgramData) => (
                                    <ProgramCard
                                        key={program.id}
                                        program={program}
                                        onViewClick={handleViewProgram}
                                        showMenu={program.programownerid === profile?.id}
                                        onEnrollLearners={() => handleOpenEnrollment(program)}
                                        banner={
                                            String(program.programownerid) === String(profile?.id)
                                                ? 'Owner'
                                                : (program.faculty ?? []).some(
                                                    faculty =>
                                                        String(faculty.id) === String(profile?.id) ||
                                                        String(faculty.userid) === String(profile?.id)
                                                )
                                                    ? 'Faculty'
                                                    : undefined
                                        }
                                    />
                                ))
                            ) : (
                                <div className={styles.emptyState}>
                                    {searchTerm
                                        ? `No results found for "${searchTerm}"`
                                        : 'No programs available.'}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            <EnrollmentModal
                isOpen={isEnrollmentOpen}
                title={selectedProgram?.title}
                users={learners}
                onClose={handleCloseEnrollment}
                onEnroll={handleEnrollLearners}
                loading={bulkEnrollLoading}
            />

            <ResponsiveModal
                open={successModal.open}
                onClose={() =>
                    setSuccessModal(prev => ({
                        ...prev,
                        open: false,
                    }))
                }
            >
                <SuccessContent
                    title={successModal.title}
                    subtitle={successModal.subtitle}
                />
            </ResponsiveModal>

            <ResponsiveModal
                open={showErrorModal}
                onClose={() => setShowErrorModal(false)}
            >
                <ErrorPopupModal
                    title={errorMessage}
                    onCancel={() => setShowErrorModal(false)}
                />
            </ResponsiveModal>
        </div>
    );
};

export default ProgramsPage;
